// src/pages/superAdmin/AgentsList.jsx
import React, { useState, useMemo, useEffect, useRef } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Link, useNavigate } from "react-router-dom";
import { 
  Card, 
  Table, 
  Row, 
  Col, 
  Form, 
  InputGroup, 
  Button, 
  Pagination, 
  Badge, 
  Modal, 
  Alert, 
  Spinner, 
  Toast, 
  ToastContainer,
  Tab,
  Tabs
} from "react-bootstrap";
import axios from "axios";
import {
  Search,
  Eye,
  FileText,
  Person,
  Pencil,
  Trash,
  CheckCircle,
  XCircle,
  Envelope,
  Phone,
  Calendar,
  ChevronDown,
  ChevronUp,
  ExclamationCircle,
  Key,
  Shield,
  Check,
  X,
  People,
  PersonPlus,
  ArrowRepeat,
  Copy,
  InfoCircle,
  Shop,
  Lock,
  ArrowLeft,
  Building,
  Globe,
  Repeat,
  Save
} from "react-bootstrap-icons";
import { API_BASE_URL } from "../constants/api_constants";

// Configuration de l'API
const USERS_ENDPOINT = `${API_BASE_URL}/users`;
const USERS_TRASHED_ENDPOINT = `${API_BASE_URL}/users/trashed`;
const MARCHES_ENDPOINT = `${API_BASE_URL}/marches`;

// Configuration des types d'agents avec icônes et couleurs
// Types d'agents avec icônes et couleurs - IDENTIQUE À AddAgent.jsx
const getAgentTypeConfig = () => ({
  admin_entite: {
    label: "Admin Economie",
    description: "Administrateur de l'entité (hors marché)",
    color: "#0d6efd",
    bgLight: "#cfe2ff",
    icon: Building,
    badge: "Hors marché",
    roleId: 1002
  },
  admin_principal: {
    label: "Admin Principal",
    description: "Administrateur principal des marchés",
    color: "#dc3545",
    bgLight: "#f8d7da",
    icon: Globe,
    badge: "ODD",
    roleId: 1003
  },
  marche: {
    label: "Agent de marché",
    description: "Agent assigné à un marché spécifique",
    color: "#0d6efd",
    bgLight: "#cfe2ff",
    icon: Shop,
    badge: "Marché",
    roleId: 1013
  },
  hors_marche: {
    label: "Agent hors marché",
    description: "Agent sans marché assigné",
    color: "#6c757d",
    bgLight: "#e9ecef",
    icon: Building,
    badge: "Hors marché",
    roleId: 1013
  }
});

// Types d'agents pour les filtres
const agentTypes = [
  { value: "admin_entite", label: "Admin Economie", description: "Administrateur de l'entité (hors marché)", roleId: 1002 },
  { value: "admin_principal", label: "Admin Principal", description: "Administrateur principal des marchés", roleId: 1003 },
  { value: "admin_marche", label: "Admin Marché", description: "Administrateur d'un marché spécifique", roleId: 1005 },
  { value: "superviseur", label: "Superviseur", description: "Superviseur d'un marché", roleId: 1007 },
  { value: "agent", label: "Agent", description: "Agent de collecte", roleId: 1012 },
  { value: "collecteur", label: "Collecteur", description: "Collecteur", roleId: 1013 }
];

// Rôles disponibles pour les filtres
const roles = [
  { value: "all", label: "Tous les rôles" },
  { value: "admin_entite", label: "Admin Economie" },
  { value: "admin_principal", label: "Admin Principal" },
  { value: "admin_marche", label: "Admin Marché" },
  { value: "superviseur", label: "Superviseur" },
  { value: "agent", label: "Agent" },
  { value: "collecteur", label: "Collecteur" },
  { value: "super_admin", label: "Super Admin" }
];

export default function AgentsList() {
  const navigate = useNavigate();
  
  // Récupérer les infos de l'utilisateur connecté
  const userData = JSON.parse(sessionStorage.getItem('userData') || '{}');
  const userRoleId = userData?.role_id || null;
  const userMarcheId = userData?.id_marche || null;
  const hasMarket = !!userMarcheId;

  // Déterminer les rôles
  const isAdminEntite = userRoleId === 1002;
  const isAdminPrincipal = userRoleId === 1003;
  const isSuperAdmin = userRoleId === 1001;
  const isAdminMarche = userRoleId === 1005;
  const isSuperviseur = userRoleId === 1007;
  const isAgent = userRoleId === 1012;
  const isCollecteur = userRoleId === 1013;

  // Vérifier si l'utilisateur a accès à la liste des agents
  const canAccessAgents = isSuperAdmin || isAdminEntite || isAdminPrincipal || isAdminMarche || isSuperviseur;

  // États principaux
  const [agents, setAgents] = useState([]);
  const [trashedAgents, setTrashedAgents] = useState([]);
  const [marches, setMarches] = useState([]);
  const [loadingMarches, setLoadingMarches] = useState(false);
  const [activeTab, setActiveTab] = useState('active');
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortField, setSortField] = useState('created_at');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // États pour les modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showBulkRestoreModal, setShowBulkRestoreModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  
  // États pour les données
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [agentToRestore, setAgentToRestore] = useState(null);
  const [agentToResetPassword, setAgentToResetPassword] = useState(null);
  const [resetPasswordResult, setResetPasswordResult] = useState(null);
  
  // États pour les formulaires
  const [newAgent, setNewAgent] = useState({
    email: '',
    full_name: '',
    admin_lname: '',
    admin_fname: '',
    admin_phone: '',
    admin_fonction: '',
    first_name: '',
    role_id: 1012,
    is_active: 1,
    admin_email: '',
    type_compte: 2,
    matricule_interne: '',
    code_agent_interne: '',
    agent_type: 'agent',
    id_marche: null
  });
  
  const [editAgent, setEditAgent] = useState({
    email: '',
    full_name: '',
    first_name: '',
    role_id: 1012,
    is_active: 1,
    admin_email: '',
    admin_lname: '',
    admin_fname: '',
    admin_phone: '',
    admin_fonction: '',
    type_compte: 2,
    matricule_interne: '',
    code_agent_interne: '',
    agent_type: 'agent',
    id_marche: null
  });



  // Déterminer les types d'agents disponibles selon le rôle - IDENTIQUE À AddAgent.jsx
const getAvailableAgentTypes = () => {
  // Super Admin peut tout créer
  if (isSuperAdmin) {
    return ['admin_entite', 'admin_principal', 'marche', 'hors_marche'];
  }
  // Admin Principal (1003) peut créer des agents de marché
  if (isAdminPrincipal) {
    return ['marche', 'admin_principal'];
  }
  // Admin Entité (1002)
  if (isAdminEntite) {
    return ['hors_marche'];
  }
  // Admin Marché (1005) ou Superviseur (1007) - UNIQUEMENT agents de marché
  if (isAdminMarche || isSuperviseur) {
    return ['marche'];
  }
  // Agent ou Collecteur (1012, 1013) - UNIQUEMENT agents hors marché
  if (userRoleId === 1012 || userRoleId === 1013) {
    return ['hors_marche'];
  }
  // Par défaut
  return ['hors_marche'];
};

  // États pour la recherche de marché dans le modal d'édition
  const [marcheSearchTerm, setMarcheSearchTerm] = useState('');
  const [showMarcheDropdown, setShowMarcheDropdown] = useState(false);
  const [selectedMarcheName, setSelectedMarcheName] = useState('');
  const marcheSearchRef = useRef(null);

  // États pour les filtres
  const [selectedRole, setSelectedRole] = useState('all');
  
  // États pour les notifications
  const [notifications, setNotifications] = useState([]);
  const [alertMessage, setAlertMessage] = useState({ show: false, message: '', type: 'info' });

  // États pour le chargement
  const [savingAgent, setSavingAgent] = useState(false);
  const [updatingAgent, setUpdatingAgent] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState(false);
  const [deletingAgents, setDeletingAgents] = useState(false);
  const [restoringAgent, setRestoringAgent] = useState(false);
  const [restoringAgents, setRestoringAgents] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  // États pour afficher les informations du nouvel agent
  const [newUserCredentials, setNewUserCredentials] = useState(null);
  const [showCredentialsPanel, setShowCredentialsPanel] = useState(false);

  const getAuthToken = () => sessionStorage.getItem("authToken");

  const getHeaders = () => {
    const token = getAuthToken();
    const headers = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  };

  // Fonction pour obtenir le role_id à partir du type d'agent
  const getRoleIdFromType = (agentType) => {
    switch (agentType) {
      case 'admin_entite':
        return 1002;
      case 'admin_principal':
        return 1003;
      case 'admin_marche':
        return 1005;
      case 'superviseur':
        return 1007;
      case 'agent':
        return 1012;
      case 'collecteur':
      default:
        return 1013;
    }
  };

  // Filtrer les marchés pour le dropdown
  const getFilteredMarches = (searchTerm) => {
    if (!searchTerm) return marches;
    return marches.filter(m => 
      m.nom_marche?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.id_marche?.toString().includes(searchTerm) ||
      (m.code_marche && m.code_marche.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  };

  // Charger les marchés
  const fetchMarches = async () => {
    try {
      setLoadingMarches(true);
      const response = await axios.get(MARCHES_ENDPOINT, { 
        headers: getHeaders(),
        params: { per_page: 100 }
      });
      
      let marchesData = [];
      if (response.data && response.data.code === 200) {
        marchesData = response.data.data || [];
      } else if (response.data && Array.isArray(response.data)) {
        marchesData = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data)) {
        marchesData = response.data.data;
      }
      
      setMarches(marchesData);
    } catch (error) {
      console.error("Erreur chargement marchés:", error);
      setMarches([]);
    } finally {
      setLoadingMarches(false);
    }
  };

  // Gestion des clics en dehors du dropdown de recherche de marché
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (marcheSearchRef.current && !marcheSearchRef.current.contains(event.target)) {
        setShowMarcheDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Configuration des badges de rôle
  const getRoleConfig = (roleId) => {
    const roleConfigs = {
      1001: { label: "Super Admin", color: "danger", icon: <Shield className="me-1" size={12} /> },
      1002: { label: "Admin Economie", color: "primary", icon: <Building className="me-1" size={12} /> },
      1003: { label: "Admin Principal", color: "danger", icon: <Globe className="me-1" size={12} /> },
      1005: { label: "Admin Marché", color: "primary", icon: <Shop className="me-1" size={12} /> },
      1007: { label: "Superviseur", color: "info", icon: <Eye className="me-1" size={12} /> },
      1012: { label: "Agent", color: "success", icon: <Person className="me-1" size={12} /> },
      1013: { label: "Collecteur", color: "warning", icon: <Person className="me-1" size={12} /> }
    };
    return roleConfigs[roleId] || { label: "Agent", color: "secondary", icon: <Person className="me-1" size={12} /> };
  };

  const getRoleBadge = (agent) => {
    const roleId = agent?.role_id;
    const config = getRoleConfig(roleId);
    return (
      <Badge bg={config.color} className="d-flex align-items-center px-2 py-1 rounded-pill" style={{ fontSize: '11px' }}>
        {config.icon}
        {config.label}
      </Badge>
    );
  };

  const formatApiData = (apiData) => {
    return apiData.map(item => {
      let status = item.is_active === 1 ? "actif" : (item.is_active === -1 ? "supprimé" : "inactif");
      
      const createdAt = item.created_at ? new Date(item.created_at).toISOString().split('T')[0] : new Date().toISOString().split('T')[0];
      const lastLogin = item.last_login_at 
        ? new Date(item.last_login_at).toLocaleString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })
        : "Jamais";
      
      let lastReset = null;
      if (item.last_reset) {
        const resetDate = new Date(item.last_reset);
        lastReset = resetDate.toLocaleDateString('fr-FR') + ' ' + resetDate.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
      }
      
      let resetByName = item.reset_by_name || (item.reset_by ? `Agent ${item.reset_by}` : null);
      
      let admin_lname = item.admin_lname || "";
      let admin_fname = item.admin_fname || "";
      
      if ((!admin_lname || !admin_fname) && item.full_name) {
        const nameParts = item.full_name.trim().split(/\s+/);
        if (nameParts.length >= 2) {
          admin_fname = nameParts[0];
          admin_lname = nameParts.slice(1).join(" ");
        } else if (nameParts.length === 1) {
          admin_lname = nameParts[0];
        }
      }
      
      return {
        id: item.id,
        user_id: item.user_id,
        entite_id: item.entite_id,
        email: item.email,
        full_name: item.full_name,
        first_name: item.first_name,
        role_id: item.role_id,
        status: status,
        is_active: item.is_active,
        type_compte: item.type_compte,
        last_login_at: lastLogin,
        created_at: createdAt,
        added_by: item.added_by,
        last_reset: lastReset,
        reset_by: resetByName,
        reset_by_id: item.reset_by,
        admin_email: item.admin_email,
        admin_lname: admin_lname,
        admin_fname: admin_fname,
        admin_phone: item.admin_phone || item.telephone,
        admin_fonction: item.admin_fonction || item.fonction,
        matricule_interne: item.matricule_interne || '',
        code_agent_interne: item.code_agent_interne || '',
        id_marche: item.id_marche || null,
        apiData: item
      };
    });
  };

  const fetchAgents = async () => {
    try {
      setLoading(true);
      setError(null);
      setAccessDenied(false);
      const response = await axios.get(USERS_ENDPOINT, { headers: getHeaders() });
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setAgents(formatApiData(response.data.data));
      } else {
        throw new Error('Format de données invalide');
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setAccessDenied(true);
        setError("Accès non autorisé. Vous n'avez pas les droits pour voir les agents.");
      } else {
        setError(err.response?.data?.message || err.message);
      }
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrashedAgents = async () => {
    try {
      const response = await axios.get(USERS_TRASHED_ENDPOINT, { headers: getHeaders() });
      if (response.data && response.data.data && Array.isArray(response.data.data)) {
        setTrashedAgents(formatApiData(response.data.data));
      }
    } catch (err) {
      if (err.response?.status === 403) {
        console.warn('Accès non autorisé pour les agents supprimés');
        setTrashedAgents([]);
      } else {
        console.error('Erreur chargement supprimés:', err);
        setTrashedAgents([]);
      }
    }
  };

  useEffect(() => {
    fetchAgents();
    fetchTrashedAgents();
    fetchMarches();
  }, []);

  // Calcul des données filtrées et triées
  const currentData = activeTab === 'active' ? agents : trashedAgents;

  const filteredAndSortedItems = useMemo(() => {
    let filtered = currentData.filter(item => {
      const searchTerm = filterText.toLowerCase();
      const matchesSearch = 
        (item.full_name && item.full_name.toLowerCase().includes(searchTerm)) ||
        (item.first_name && item.first_name.toLowerCase().includes(searchTerm)) ||
        (item.admin_lname && item.admin_lname.toLowerCase().includes(searchTerm)) ||
        (item.admin_fname && item.admin_fname.toLowerCase().includes(searchTerm)) ||
        (item.email && item.email.toLowerCase().includes(searchTerm)) ||
        (item.user_id && item.user_id.toString().includes(filterText)) ||
        (item.admin_phone && item.admin_phone.includes(filterText)) ||
        (item.matricule_interne && item.matricule_interne.toLowerCase().includes(searchTerm)) ||
        (item.code_agent_interne && item.code_agent_interne.toLowerCase().includes(searchTerm));
      
      if (selectedRole !== 'all') {
        const roleMap = {
          'admin_entite': 1002,
          'admin_principal': 1003,
          'admin_marche': 1005,
          'superviseur': 1007,
          'agent': 1012,
          'collecteur': 1013,
          'super_admin': 1001
        };
        const roleId = roleMap[selectedRole];
        if (roleId && item.role_id !== roleId) return false;
      }
      
      return matchesSearch;
    });

    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();
      return sortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });

    return filtered;
  }, [currentData, filterText, selectedRole, activeTab, sortField, sortDirection]);

  const totalPages = Math.ceil(filteredAndSortedItems.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentItems = filteredAndSortedItems.slice(startIndex, startIndex + itemsPerPage);

  const isAllSelected = currentItems.length > 0 && selectedAgents.length === currentItems.length && currentItems.every(agent => selectedAgents.includes(agent.user_id));
  const isSomeSelected = selectedAgents.length > 0 && selectedAgents.length < currentItems.length;

  // Si l'utilisateur n'a pas les droits
  if (!canAccessAgents) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
          <div className="container-fluid">
            <Card className="border-0 shadow-sm rounded-3 text-center p-5">
              <div className="mb-4">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ width: 80, height: 80 }}>
                  <Lock size={40} className="text-danger" />
                </div>
              </div>
              <h4 className="fw-bold text-danger">Accès non autorisé</h4>
              <p className="text-muted mb-4 max-width-500 mx-auto">
                Vous n'avez pas les droits nécessaires pour accéder à la liste des agents.
              </p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  <ArrowLeft className="me-2" size={16} />
                  Retour au tableau de bord
                </Button>
              </div>
            </Card>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // Si accès refusé par l'API (403)
  if (accessDenied) {
    return (
      <React.Fragment>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
          <div className="container-fluid">
            <Card className="border-0 shadow-sm rounded-3 text-center p-5">
              <div className="mb-4">
                <div className="bg-danger bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ width: 80, height: 80 }}>
                  <Lock size={40} className="text-danger" />
                </div>
              </div>
              <h4 className="fw-bold text-danger">Accès non autorisé</h4>
              <p className="text-muted mb-4 max-width-500 mx-auto">{error}</p>
              <div className="d-flex justify-content-center gap-3 flex-wrap">
                <Button variant="primary" onClick={() => navigate('/dashboard')}>
                  <ArrowLeft className="me-2" size={16} />
                  Retour au tableau de bord
                </Button>
                <Button variant="outline-secondary" onClick={() => window.location.reload()}>
                  <Repeat className="me-2" size={16} />
                  Réessayer
                </Button>
              </div>
            </Card>
          </div>
        </div>
        <Footer />
      </React.Fragment>
    );
  }

  // Fonctions utilitaires
  const showNotification = (message, type = 'info', duration = 5000) => {
    const id = Date.now();
    setNotifications(prev => [{ 
      id, 
      message, 
      type, 
      show: true,
      timestamp: Date.now()
    }, ...prev]);
    
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, duration);
  };

  const showAlert = (message, type = 'warning', duration = 5000) => {
    setAlertMessage({ show: true, message, type });
    if (duration > 0) setTimeout(() => setAlertMessage({ show: false, message: '', type: 'info' }), duration);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedAgents(currentItems.map(agent => agent.user_id));
    else setSelectedAgents([]);
  };

  const handleSelectAgent = (userId) => {
    setSelectedAgents(prev => prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]);
  };

  const getAgentTypeLabel = (agentType) => {
    const type = agentTypes.find(t => t.value === agentType);
    return type ? type.label : agentType;
  };

  const handleAddAgent = () => {
    setNewAgent({ 
      email: '', 
      full_name: '', 
      role_id: 1012, 
      is_active: 1, 
      admin_email: '', 
      admin_lname: '', 
      admin_fname: '', 
      admin_phone: '', 
      admin_fonction: '', 
      type_compte: 2,
      matricule_interne: '',
      code_agent_interne: '',
      agent_type: 'agent',
      id_marche: null
    });
    setShowAddModal(true);
  };

  const handleSaveAgent = async () => {
    if (!newAgent.admin_lname.trim() || !newAgent.admin_fname.trim() || !newAgent.admin_phone.trim() || !newAgent.admin_fonction.trim()) {
      showAlert("Veuillez remplir tous les champs obligatoires", 'warning');
      return;
    }

    if ((newAgent.agent_type === 'admin_marche' || newAgent.agent_type === 'superviseur') && !newAgent.id_marche) {
      showAlert("Veuillez sélectionner un marché pour l'agent", 'warning');
      return;
    }

    if (newAgent.agent_type === 'admin_entite' && newAgent.id_marche) {
      showAlert("Un Admin Entité ne peut pas être associé à un marché", 'warning');
      return;
    }

    if (newAgent.agent_type === 'admin_principal' && newAgent.id_marche) {
      showAlert("Un Admin Principal ne peut pas être associé à un marché spécifique", 'warning');
      return;
    }

    setSavingAgent(true);
    
    const roleId = getRoleIdFromType(newAgent.agent_type);
    
    const agentData = {
      full_name: newAgent.admin_lname.trim(),
      first_name: newAgent.admin_fname.trim(),
      fonction: newAgent.admin_fonction.trim(),
      telephone: newAgent.admin_phone.trim(),
      email: newAgent.email.trim(),
      matricule_interne: newAgent.matricule_interne.trim(),
      code_agent_interne: newAgent.code_agent_interne.trim(),
      role_id: roleId,
      id_marche: (newAgent.agent_type === 'admin_marche' || newAgent.agent_type === 'superviseur') ? newAgent.id_marche : null
    };
    
    try {
      const response = await axios.post(USERS_ENDPOINT, agentData, { headers: getHeaders() });
      if (response.data) {
        const passwordTemp = response.data.password_temp || response.data.temp_password;
        setNewUserCredentials({
          admin_fname: newAgent.admin_fname,
          admin_lname: newAgent.admin_lname,
          email: response.data.user?.email || newAgent.email,
          admin_phone: newAgent.admin_phone,
          password_temp: passwordTemp || 'Non fourni',
          matricule_interne: newAgent.matricule_interne,
          code_agent_interne: newAgent.code_agent_interne,
          agent_type: newAgent.agent_type,
          marche_nom: newAgent.id_marche ? marches.find(m => m.id_marche === newAgent.id_marche)?.nom_marche : null,
          role_id: roleId
        });
        setShowCredentialsPanel(true);
        await fetchAgents();
        await fetchTrashedAgents();
        setShowAddModal(false);
        showNotification('Agent créé avec succès', 'success');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        showNotification("Accès non autorisé. Vous ne pouvez pas créer d'agent.", 'danger');
      } else {
        showNotification(error.response?.data?.message || 'Erreur lors de la création', 'danger');
      }
    } finally {
      setSavingAgent(false);
    }
  };

const handleEditAgent = (agent) => {
  setSelectedAgent(agent);
  
  // Déterminer le type d'agent en fonction du role_id
  let agentType = 'hors_marche';
  if (agent.role_id === 1002) {
    agentType = 'admin_entite';
  } else if (agent.role_id === 1003) {
    agentType = 'admin_principal';
  } else if (agent.role_id === 1005) {
    agentType = 'marche';
  } else if (agent.role_id === 1007) {
    agentType = 'marche';
  } else if (agent.role_id === 1012) {
    agentType = 'marche';
  } else if (agent.role_id === 1013) {
    agentType = 'marche';
  }
  
  // Pour les agents avec role_id 1012 ou 1013, vérifier s'ils ont un marché
  if ((agent.role_id === 1012 || agent.role_id === 1013) && agent.id_marche) {
    agentType = 'marche';
  } else if ((agent.role_id === 1012 || agent.role_id === 1013) && !agent.id_marche) {
    agentType = 'hors_marche';
  }
  
  setEditAgent({
    email: agent.email || '',
    full_name: agent.full_name || '',
    first_name: agent.first_name || '',
    admin_lname: agent.admin_lname || '',
    admin_fname: agent.admin_fname || '',
    admin_phone: agent.admin_phone || '',
    admin_fonction: agent.admin_fonction || '',
    role_id: agent.role_id || 1012,
    is_active: agent.is_active !== undefined ? agent.is_active : 1,  // ← CONSERVER LA VALEUR
    admin_email: agent.admin_email || '',
    type_compte: agent.type_compte || 2,
    matricule_interne: agent.matricule_interne || '',
    code_agent_interne: agent.code_agent_interne || '',
    agent_type: agentType,
    id_marche: agent.id_marche || null
  });
  
  // Pré-remplir la recherche de marché
  if (agent.id_marche) {
    const marche = marches.find(m => m.id_marche === agent.id_marche);
    if (marche) {
      setSelectedMarcheName(marche.nom_marche);
      setMarcheSearchTerm(marche.nom_marche);
    }
  }
  
  setShowEditModal(true);
};

const handleUpdateAgent = async () => {
  if (!selectedAgent) return;
  setUpdatingAgent(true);
  
  const roleId = getRoleIdFromType(editAgent.agent_type);
  
  const updateData = {
    full_name: editAgent.admin_lname,
    first_name: editAgent.admin_fname,
    fonction: editAgent.admin_fonction,
    telephone: editAgent.admin_phone,
    email: editAgent.email,
    matricule_interne: editAgent.matricule_interne,
    code_agent_interne: editAgent.code_agent_interne,
    role_id: roleId,
    id_marche: (editAgent.agent_type === 'admin_marche' || editAgent.agent_type === 'superviseur') ? editAgent.id_marche : null,
    is_active: editAgent.is_active || 1  // ← AJOUTER CETTE LIGNE
  };
  
  try {
    await axios.put(`${USERS_ENDPOINT}/${selectedAgent.user_id}`, updateData, { headers: getHeaders() });
    await fetchAgents();
    await fetchTrashedAgents();
    setShowEditModal(false);
    showNotification('Agent mis à jour avec succès', 'success');
  } catch (error) {
    if (error.response?.status === 403) {
      showAlert("Accès non autorisé. Vous ne pouvez pas modifier cet agent.", 'danger');
    } else {
      showAlert(error.response?.data?.message || 'Erreur lors de la mise à jour', 'danger');
    }
  } finally {
    setUpdatingAgent(false);
  }
};

  const handleDeleteAgent = (agent) => {
    setAgentToDelete(agent);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!agentToDelete) return;
    setDeletingAgent(true);
    try {
      await axios.delete(`${USERS_ENDPOINT}/${agentToDelete.user_id}`, { headers: getHeaders() });
      await fetchAgents();
      await fetchTrashedAgents();
      setShowDeleteModal(false);
      showNotification('Agent supprimé avec succès', 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas supprimer cet agent.", 'danger');
      } else {
        showAlert(error.response?.data?.message || 'Erreur lors de la suppression', 'danger');
      }
    } finally {
      setDeletingAgent(false);
    }
  };

  const handleRestoreAgent = (agent) => {
    setAgentToRestore(agent);
    setShowRestoreModal(true);
  };

  const handleConfirmRestore = async () => {
    if (!agentToRestore) return;
    setRestoringAgent(true);
    try {
      await axios.post(`${USERS_ENDPOINT}/${agentToRestore.user_id}/restore`, {}, { headers: getHeaders() });
      await fetchAgents();
      await fetchTrashedAgents();
      setSelectedAgents([]);
      setShowRestoreModal(false);
      showNotification('Agent restauré avec succès', 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas restaurer cet agent.", 'danger');
      } else {
        showAlert(error.response?.data?.message || 'Erreur lors de la restauration', 'danger');
      }
    } finally {
      setRestoringAgent(false);
    }
  };

  const handleBulkDelete = () => {
    if (selectedAgents.length === 0) {
      showAlert("Veuillez sélectionner au moins un agent", 'warning');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const handleConfirmBulkDelete = async () => {
    setDeletingAgents(true);
    try {
      await Promise.all(selectedAgents.map(userId => axios.delete(`${USERS_ENDPOINT}/${userId}`, { headers: getHeaders() })));
      await fetchAgents();
      await fetchTrashedAgents();
      setSelectedAgents([]);
      setShowBulkDeleteModal(false);
      showNotification(`${selectedAgents.length} agent(s) supprimé(s) avec succès`, 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas effectuer cette action.", 'danger');
      } else {
        showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
      }
      await fetchAgents();
      await fetchTrashedAgents();
      setSelectedAgents([]);
    } finally {
      setDeletingAgents(false);
    }
  };

  const handleBulkRestore = () => {
    if (selectedAgents.length === 0) {
      showAlert("Veuillez sélectionner au moins un agent", 'warning');
      return;
    }
    setShowBulkRestoreModal(true);
  };

  const handleConfirmBulkRestore = async () => {
    setRestoringAgents(true);
    try {
      await Promise.all(selectedAgents.map(userId => axios.post(`${USERS_ENDPOINT}/${userId}/restore`, {}, { headers: getHeaders() })));
      await fetchAgents();
      await fetchTrashedAgents();
      setSelectedAgents([]);
      setShowBulkRestoreModal(false);
      showNotification(`${selectedAgents.length} agent(s) restauré(s) avec succès`, 'success');
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas effectuer cette action.", 'danger');
      } else {
        showAlert(`Erreur: ${error.response?.data?.message || error.message}`, 'danger');
      }
      await fetchAgents();
      await fetchTrashedAgents();
      setSelectedAgents([]);
    } finally {
      setRestoringAgents(false);
    }
  };

  const handleResetPassword = (agent) => {
    setAgentToResetPassword(agent);
    setShowResetPasswordModal(true);
  };

  const handleConfirmResetPassword = async () => {
    if (!agentToResetPassword) return;
    setResettingPassword(true);
    try {
      const response = await axios.post(
        `${USERS_ENDPOINT}/${agentToResetPassword.user_id}/reset-password`,
        { user_id: agentToResetPassword.user_id },
        { headers: getHeaders() }
      );
      if (response.data && response.data.password_show) {
        await fetchAgents();
        await fetchTrashedAgents();
        setResetPasswordResult(response.data);
        setShowResetPasswordModal(false);
        setShowPasswordModal(true);
        showNotification('Mot de passe réinitialisé avec succès', 'success');
      }
    } catch (error) {
      if (error.response?.status === 403) {
        showAlert("Accès non autorisé. Vous ne pouvez pas réinitialiser ce mot de passe.", 'danger');
      } else {
        showAlert(error.response?.data?.message || 'Erreur lors de la réinitialisation', 'danger');
      }
    } finally {
      setResettingPassword(false);
    }
  };

  const SortableHeader = ({ field, children }) => (
    <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(field)}>
      {children}
      {sortField === field && (sortDirection === 'asc' ? <ChevronUp size={12} className="ms-1" /> : <ChevronDown size={12} className="ms-1" />)}
    </th>
  );

  const DataWithIcon = ({ value, icon: Icon, emptyText = "—", iconSize = 12 }) => {
    if (!value || value === "Jamais") return <span className="text-muted small">{emptyText}</span>;
    return <div className="d-flex align-items-center text-muted"><Icon size={iconSize} className="me-1" /><small>{value}</small></div>;
  };

  // RENDU PRINCIPAL
  return (
    <React.Fragment>
      <Header />
      <style>{`
        .custom-table th {
          background: #f8f9fa;
          font-weight: 600;
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e9ecef;
        }
        .custom-table td {
          vertical-align: middle;
          padding: 0.75rem;
          font-size: 0.85rem;
        }
        .custom-table tbody tr:hover {
          background-color: rgba(13, 110, 253, 0.04);
          transition: background-color 0.2s ease;
        }
        .rounded-checkbox {
          width: 18px;
          height: 18px;
          border-radius: 6px !important;
          cursor: pointer;
          transition: all 0.2s ease;
        }
        .rounded-checkbox:checked {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        .rounded-checkbox:focus {
          box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
        }
        .avatar-circle {
          width: 36px;
          height: 36px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: bold;
          font-size: 14px;
        }
        .agent-name {
          font-weight: 600;
          margin-bottom: 2px;
          font-size: 0.9rem;
        }
        .agent-id {
          font-size: 0.7rem;
          color: #6c757d;
        }
        .action-btn {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
        }
        .action-btn:hover {
          transform: translateY(-2px);
        }
        .nav-tabs .nav-link {
          font-weight: 500;
          border: none;
          color: #6c757d;
          padding: 10px 20px;
          border-radius: 8px 8px 0 0;
        }
        .nav-tabs .nav-link.active {
          color: #0d6efd;
          background: white;
          border-bottom: 3px solid #0d6efd;
        }
        .nav-tabs .nav-link:hover:not(.active) {
          background: #f8f9fa;
          color: #495057;
        }
        .max-width-500 {
          max-width: 500px;
        }
        .transition-all {
          transition: all 0.3s ease !important;
        }
        .cursor-pointer {
          cursor: pointer !important;
        }
        .hover-bg-light:hover {
          background-color: #f8f9fa !important;
        }
        .shadow-lg {
          box-shadow: 0 8px 30px rgba(0,0,0,0.12) !important;
        }
      `}</style>

      <div className="main main-app p-3 p-lg-4" style={{ background: '#f5f7fa', minHeight: '100vh' }}>
        <ToastContainer className="p-3" style={{ zIndex: 9999, position: 'fixed', top: '20px', right: '20px' }}>
          {notifications.map((notification) => (
            <Toast key={notification.id} show={notification.show} onClose={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))} delay={8000} autohide>
              <Toast.Header className={`bg-${notification.type === 'success' ? 'success' : notification.type === 'error' ? 'danger' : notification.type === 'warning' ? 'warning' : 'info'} text-white`}>
                <strong className="me-auto">{notification.type === 'success' ? 'Succès' : notification.type === 'error' ? 'Erreur' : 'Information'}</strong>
              </Toast.Header>
              <Toast.Body className="text-dark">{notification.message}</Toast.Body>
            </Toast>
          ))}
        </ToastContainer>

        {alertMessage.show && (
          <div className="container-fluid mb-3">
            <Alert variant={alertMessage.type} dismissible onClose={() => setAlertMessage({ show: false, message: '', type: 'info' })} className="shadow-sm border-0">
              <div className="d-flex align-items-center">
                {alertMessage.type === 'success' ? <Check className="me-2 fs-5" /> :
                 alertMessage.type === 'danger' ? <X className="me-2 fs-5" /> :
                 alertMessage.type === 'warning' ? <ExclamationCircle className="me-2 fs-5" /> :
                 <CheckCircle className="me-2 fs-5" />}
                <span className="fw-medium">{alertMessage.message}</span>
              </div>
            </Alert>
          </div>
        )}

        <div className="container-fluid">
          <Row className="mb-4">
            <Col md={12}>
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center">
                <div className="mb-3 mb-md-0">
                  <ol className="breadcrumb bg-transparent p-0 mb-1">
                    <li className="breadcrumb-item"><Link to="" className="text-decoration-none">Tableau de bord</Link></li>
                    <li className="breadcrumb-item active text-primary" aria-current="page">Gestion des Agents</li>
                  </ol>
                  <h4 className="main-title mb-0 fw-bold">Gestion des Agents</h4>
                  <p className="text-muted mb-0 small">Administrez les comptes agents et leurs permissions</p>
                </div>
                <div className="d-flex flex-wrap gap-2">
                  {selectedAgents.length > 0 && activeTab === 'active' && (
                    <Button variant="outline-danger" size="sm" onClick={handleBulkDelete} className="rounded-pill">
                      <Trash className="me-2" size={14} /> Supprimer ({selectedAgents.length})
                    </Button>
                  )}
                  {selectedAgents.length > 0 && activeTab === 'trashed' && (
                    <Button variant="outline-success" size="sm" onClick={handleBulkRestore} className="rounded-pill">
                      <ArrowRepeat className="me-2" size={14} /> Restaurer ({selectedAgents.length})
                    </Button>
                  )}
                  <Button 
                    variant="success" 
                    className="rounded-pill" 
                    onClick={() => navigate('/superAdmin/agent/new')}
                  >
                    <PersonPlus className="me-2" size={16} /> Nouvel Agent
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          {/* Panneau des identifiants */}
          {showCredentialsPanel && newUserCredentials && (
            <div className="container-fluid mb-3">
              <Card className="border border-success shadow-lg rounded-3">
                <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center py-3 rounded-top-3">
                  <div className="d-flex align-items-center">
                    <PersonPlus className="me-2" size={20} />
                    <strong>NOUVEL AGENT CRÉÉ AVEC SUCCÈS</strong>
                  </div>
                  <Button variant="outline-light" size="sm" onClick={() => setShowCredentialsPanel(false)} className="rounded-circle"><X size={16} /></Button>
                </Card.Header>
                <Card.Body className="bg-light">
                  <Row className="justify-content-center">
                    <Col md={8}>
                      <Card className="border-0 shadow-sm rounded-3">
                        <Card.Body>
                          <table className="table table-borderless">
                            <tbody>
                              <tr><td className="fw-bold text-muted"><Person className="me-2 text-primary" size={18} />Prénom :</td><td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.admin_fname || ''}</strong></td></tr>
                              <tr><td className="fw-bold text-muted"><Person className="me-2 text-primary" size={18} />Nom :</td><td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.admin_lname || ''}</strong></td></tr>
                              <tr><td className="fw-bold text-muted"><Envelope className="me-2 text-primary" size={18} />Email :</td><td><div className="d-flex align-items-center bg-white p-2 rounded-3"><span className="flex-grow-1">{newUserCredentials.email}</span><Button size="sm" variant="link" className="p-0 ms-2 text-primary" onClick={() => { navigator.clipboard.writeText(newUserCredentials.email); showNotification('Email copié !', 'success'); }}><FileText size={14} /></Button></div></td></tr>
                              <tr><td className="fw-bold text-muted"><Phone className="me-2 text-primary" size={18} />Téléphone :</td><td><div className="d-flex align-items-center bg-white p-2 rounded-3"><span className="flex-grow-1">{newUserCredentials.admin_phone}</span><Button size="sm" variant="link" className="p-0 ms-2 text-primary" onClick={() => { navigator.clipboard.writeText(newUserCredentials.admin_phone); showNotification('Téléphone copié !', 'success'); }}><FileText size={14} /></Button></div></td></tr>
                              <tr><td className="fw-bold text-muted"><Key className="me-2 text-warning" size={18} />Mot de passe :</td><td><div className="d-flex align-items-center bg-warning bg-opacity-10 p-2 rounded-3 border border-warning"><span className="flex-grow-1 font-monospace fw-bold text-warning">{newUserCredentials.password_temp}</span><Button size="sm" variant="link" className="p-0 ms-2" onClick={() => { navigator.clipboard.writeText(newUserCredentials.password_temp.toString()); showNotification('Mot de passe copié !', 'success'); }}><FileText size={14} className="text-warning" /></Button></div></td></tr>
                              <tr><td className="fw-bold text-muted"><Shield className="me-2 text-info" size={18} />Type d'agent :</td><td className="bg-white p-2 rounded-3">
                                <Badge bg={newUserCredentials.agent_type === 'admin_entite' ? 'primary' : newUserCredentials.agent_type === 'admin_principal' ? 'danger' : newUserCredentials.agent_type === 'admin_marche' ? 'primary' : newUserCredentials.agent_type === 'superviseur' ? 'info' : newUserCredentials.agent_type === 'agent' ? 'success' : 'warning'}>
                                  {getAgentTypeLabel(newUserCredentials.agent_type)}
                                </Badge>
                                {newUserCredentials.marche_nom && (
                                  <Badge bg="info" className="ms-2">
                                    <Shop className="me-1" size={12} /> {newUserCredentials.marche_nom}
                                  </Badge>
                                )}
                              </td></tr>
                              {newUserCredentials.matricule_interne && (
                                <tr><td className="fw-bold text-muted"><Key className="me-2 text-info" size={18} />Matricule Interne :</td><td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.matricule_interne}</strong></td></tr>
                              )}
                              {newUserCredentials.code_agent_interne && (
                                <tr><td className="fw-bold text-muted"><Key className="me-2 text-info" size={18} />Code Agent Interne :</td><td className="bg-white p-2 rounded-3"><strong>{newUserCredentials.code_agent_interne}</strong></td></tr>
                              )}
                            </tbody>
                          </table>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                  <Row className="mt-3">
                    <Col md={12} className="d-flex justify-content-center gap-2">
                      <Button variant="outline-primary" onClick={() => { const text = `Bonjour ${newUserCredentials.admin_fname},\n\nVotre compte agent a été créé.\n\nType: ${getAgentTypeLabel(newUserCredentials.agent_type)}\n${newUserCredentials.marche_nom ? `Marché: ${newUserCredentials.marche_nom}\n` : ''}\n\nIdentifiants :\nEmail : ${newUserCredentials.email}\nMot de passe : ${newUserCredentials.password_temp}\n${newUserCredentials.matricule_interne ? `Matricule interne : ${newUserCredentials.matricule_interne}\n` : ''}${newUserCredentials.code_agent_interne ? `Code agent interne : ${newUserCredentials.code_agent_interne}\n` : ''}\nLien : ${window.location.origin}/login\n\nChangez votre mot de passe à la première connexion.`; navigator.clipboard.writeText(text); showNotification('Message copié !', 'success'); }} className="rounded-pill"><FileText className="me-2" size={14} />Copier le message</Button>
                      <Button variant="success" onClick={() => { setShowCredentialsPanel(false); setNewUserCredentials(null); }} className="rounded-pill"><Check className="me-2" size={14} />Fermer</Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </div>
          )}

          {/* Onglets */}
          <Row className="mb-3">
            <Col md={12}>
              <Tabs activeKey={activeTab} onSelect={(k) => { setActiveTab(k); setSelectedAgents([]); setCurrentPage(1); }} className="border-0 bg-white rounded-3 shadow-sm">
                <Tab 
                  eventKey="active" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <People className="text-primary" size={18} />
                      <span>Actifs / Inactifs</span>
                      <Badge bg="primary" className="rounded-pill ms-1">{agents.length}</Badge>
                    </span>
                  } 
                />
                <Tab 
                  eventKey="trashed" 
                  title={
                    <span className="d-flex align-items-center gap-2">
                      <Trash className="text-secondary" size={18} />
                      <span>Supprimés</span>
                      <Badge bg="secondary" className="rounded-pill ms-1">{trashedAgents.length}</Badge>
                    </span>
                  } 
                />
              </Tabs>
            </Col>
          </Row>

          {/* Barre de recherche et filtres */}
          <Row className="mb-4">
            <Col md={12}>
              <Card className="border-0 shadow-sm rounded-3">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={5}>
                      <InputGroup className="rounded-3 overflow-hidden">
                        <InputGroup.Text className="bg-white border-end-0">
                          <Search className="text-primary" size={18} />
                        </InputGroup.Text>
                        <Form.Control 
                          type="text" 
                          placeholder="Rechercher un agent..." 
                          value={filterText} 
                          onChange={(e) => setFilterText(e.target.value)}
                          className="border-start-0"
                        />
                      </InputGroup>
                    </Col>
                    <Col md={5}>
                      <Form.Select value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)} className="rounded-3">
                        {roles.map(role => <option key={role.value} value={role.value}>{role.label}</option>)}
                      </Form.Select>
                    </Col>
                    <Col md={2} className="d-flex gap-2">
                      <Button variant="outline-secondary" onClick={() => { setFilterText(""); setSelectedRole("all"); }} className="rounded-3">
                        <X size={14} />
                      </Button>
                      <Button variant="primary" onClick={() => { fetchAgents(); fetchTrashedAgents(); }} className="rounded-3">
                        <ArrowRepeat size={14} />
                      </Button>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tableau principal */}
          <Row>
            <Col md={12}>
              <Card className="shadow-sm border-0 rounded-3 overflow-hidden">
                <Card.Header className="bg-white border-0 py-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <People className="text-primary me-2" size={20} />
                      <span className="fw-semibold">Liste des Agents</span>
                      <Badge bg="light" text="dark" className="ms-2 rounded-pill">
                        {filteredAndSortedItems.length} au total
                      </Badge>
                    </div>
                    {selectedAgents.length > 0 && (
                      <Badge bg="primary" className="rounded-pill">
                        {selectedAgents.length} sélectionné(s)
                      </Badge>
                    )}
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <div className="table-responsive">
                    <Table hover className="custom-table mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              className="form-check-input rounded-checkbox"
                              checked={isAllSelected}
                              ref={input => { if (input) input.indeterminate = isSomeSelected; }}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <SortableHeader field="full_name">Agent</SortableHeader>
                          <SortableHeader field="matricule_interne">Mat. Interne</SortableHeader>
                          <SortableHeader field="code_agent_interne">Code Agent</SortableHeader>
                          <SortableHeader field="last_login_at">Dern. conn.</SortableHeader>
                          <SortableHeader field="last_reset">Dern. reset</SortableHeader>
                          <SortableHeader field="reset_by">Reset par</SortableHeader>
                          <th style={{ width: '130px' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loading ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <Spinner animation="border" variant="primary" />
                              <p className="mt-2 text-muted small">Chargement des agents...</p>
                            </td>
                          </tr>
                        ) : error && currentData.length === 0 ? (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <ExclamationCircle size={48} className="text-danger mb-3" />
                              <h5>Erreur de chargement</h5>
                              <p>{error}</p>
                              <Button variant="primary" onClick={fetchAgents} className="rounded-3">Réessayer</Button>
                            </td>
                          </tr>
                        ) : currentItems.length > 0 ? (
                          currentItems.map((agent) => (
                            <tr key={agent.user_id}>
                              <td>
                                <input
                                  type="checkbox"
                                  className="form-check-input rounded-checkbox"
                                  checked={selectedAgents.includes(agent.user_id)}
                                  onChange={() => handleSelectAgent(agent.user_id)}
                                />
                              </td>
                              <td>
                                <div className="d-flex align-items-center">
                                  <div className="avatar-circle me-3">
                                    {agent.admin_fname?.charAt(0) || agent.full_name?.charAt(0) || "A"}
                                  </div>
                                  <div>
                                    <div className="agent-name">{agent.admin_fname} {agent.admin_lname}</div>
                                    <div className="agent-id">
                                      <Envelope size={10} className="me-1" />
                                      {agent.email}
                                    </div>
                                    <div className="agent-id mt-1 d-flex flex-wrap gap-1">
                                      {getRoleBadge(agent)}
                                      {agent.id_marche && (
                                        <Badge bg="info" className="d-flex align-items-center px-2 py-1 rounded-pill" style={{ fontSize: '9px' }}>
                                          <Shop className="me-1" size={8} /> Marché
                                        </Badge>
                                      )}
                                      {!agent.id_marche && agent.role_id !== 1001 && agent.role_id !== 1002 && (
                                        <Badge bg="secondary" className="d-flex align-items-center px-2 py-1 rounded-pill" style={{ fontSize: '9px' }}>
                                          <Building className="me-1" size={8} /> Hors marché
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td><DataWithIcon value={agent.matricule_interne} icon={FileText} emptyText="—" /></td>
                              <td><DataWithIcon value={agent.code_agent_interne} icon={Key} emptyText="—" /></td>
                              <td><DataWithIcon value={agent.last_login_at} icon={Calendar} emptyText="Jamais" /></td>
                              <td><DataWithIcon value={agent.last_reset} icon={Calendar} /></td>
                              <td><DataWithIcon value={agent.reset_by} icon={Person} /></td>
                              <td>
                                <div className="d-flex gap-2">
                                  {activeTab === 'active' ? (
                                    <>
                                      <Button as={Link} to={`/agent/agent-details/${agent.user_id}`} size="sm" variant="outline-primary" title="Voir détails" className="action-btn">
                                        <Eye size={14} />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="outline-info" 
                                        title="Modifier" 
                                        onClick={() => handleEditAgent(agent)}
                                        className="action-btn"
                                      >
                                        <Pencil size={14} />
                                      </Button>
                                      <Button size="sm" variant="outline-secondary" title="Réinitialiser mot de passe" onClick={() => handleResetPassword(agent)} className="action-btn">
                                        <Key size={14} />
                                      </Button>
                                      <Button size="sm" variant="outline-danger" title="Supprimer" onClick={() => handleDeleteAgent(agent)} className="action-btn">
                                        <Trash size={14} />
                                      </Button>
                                    </>
                                  ) : (
                                    <Button size="sm" variant="outline-success" title="Restaurer" onClick={() => handleRestoreAgent(agent)} className="action-btn">
                                      <ArrowRepeat size={14} />
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-5">
                              <People size={48} className="text-muted mb-3" />
                              <p className="mt-3 text-muted">AUCUN AGENT TROUVÉ.</p>
                              {(filterText || selectedRole !== 'all') && (
                                <Button variant="outline-primary" size="sm" onClick={() => { setFilterText(''); setSelectedRole('all'); }} className="rounded-3">
                                  Réinitialiser les filtres
                                </Button>
                              )}
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div className="p-3 border-top d-flex justify-content-between align-items-center flex-wrap gap-3 bg-light">
                      <div className="text-muted small">
                        Affichage de <strong>{startIndex + 1}</strong> à <strong>{Math.min(startIndex + itemsPerPage, filteredAndSortedItems.length)}</strong> sur <strong>{filteredAndSortedItems.length}</strong> agents
                        {selectedAgents.length > 0 && <span className="ms-2 text-primary">• {selectedAgents.length} sélectionné(s)</span>}
                      </div>
                      
                      <div className="d-flex align-items-center gap-3">
                        <Form.Select 
                          size="sm" 
                          value={itemsPerPage} 
                          onChange={(e) => {
                            setItemsPerPage(Number(e.target.value));
                            setCurrentPage(1);
                          }}
                          className="rounded-3"
                          style={{ width: 'auto' }}
                        >
                          <option value={5}>5 par page</option>
                          <option value={10}>10 par page</option>
                          <option value={25}>25 par page</option>
                          <option value={50}>50 par page</option>
                          <option value={100}>100 par page</option>
                        </Form.Select>
                        
                        <Pagination className="mb-0">
                          <Pagination.First disabled={currentPage === 1} onClick={() => setCurrentPage(1)} className="rounded" />
                          <Pagination.Prev disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)} className="rounded" />
                          
                          {currentPage > 2 && <Pagination.Item onClick={() => setCurrentPage(1)} className="rounded">{1}</Pagination.Item>}
                          {currentPage > 3 && <Pagination.Ellipsis disabled />}
                          {currentPage > 1 && <Pagination.Item onClick={() => setCurrentPage(currentPage - 1)} className="rounded">{currentPage - 1}</Pagination.Item>}
                          <Pagination.Item active className="rounded">{currentPage}</Pagination.Item>
                          {currentPage < totalPages && <Pagination.Item onClick={() => setCurrentPage(currentPage + 1)} className="rounded">{currentPage + 1}</Pagination.Item>}
                          {currentPage < totalPages - 2 && <Pagination.Ellipsis disabled />}
                          {currentPage < totalPages - 1 && <Pagination.Item onClick={() => setCurrentPage(totalPages)} className="rounded">{totalPages}</Pagination.Item>}
                          
                          <Pagination.Next disabled={currentPage === totalPages} onClick={() => setCurrentPage(currentPage + 1)} className="rounded" />
                          <Pagination.Last disabled={currentPage === totalPages} onClick={() => setCurrentPage(totalPages)} className="rounded" />
                        </Pagination>
                      </div>
                    </div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </div>

        {/* MODAL D'AJOUT - Version simplifiée */}
        <Modal show={showAddModal} onHide={() => setShowAddModal(false)} size="lg">
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title><PersonPlus className="me-2" />Ajouter un nouvel agent</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="mb-4">
              <h6 className="text-muted mb-3 border-bottom pb-2">
                <Shield className="me-2" size={16} /> Type d'agent
              </h6>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Type d'agent <span className="text-danger">*</span></Form.Label>
                    <Row className="g-3">
                      {agentTypes.map((type) => (
                        <Col key={type.value} lg={4} md={6} sm={12}>
                          <div 
                            className={`p-3 border rounded-3 h-100 w-100 ${newAgent.agent_type === type.value ? 'border-2' : ''}`}
                            style={{
                              backgroundColor: newAgent.agent_type === type.value ? 'rgba(13, 110, 253, 0.05)' : 'transparent',
                              borderColor: newAgent.agent_type === type.value ? '#0d6efd' : '#dee2e6',
                              borderWidth: newAgent.agent_type === type.value ? '2px' : '1px',
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => {
                              setNewAgent({
                                ...newAgent,
                                agent_type: type.value,
                                id_marche: (type.value === 'admin_marche' || type.value === 'superviseur') ? newAgent.id_marche : null
                              });
                            }}
                          >
                            <Form.Check
                              type="radio"
                              id={`agent-type-${type.value}`}
                              label={
                                <div>
                                  <span className="fw-medium">{type.label}</span>
                                  <br />
                                  <small className="text-muted">{type.description}</small>
                                </div>
                              }
                              value={type.value}
                              checked={newAgent.agent_type === type.value}
                              onChange={() => {
                                setNewAgent({
                                  ...newAgent,
                                  agent_type: type.value,
                                  id_marche: (type.value === 'admin_marche' || type.value === 'superviseur') ? newAgent.id_marche : null
                                });
                              }}
                              className="w-100"
                              style={{ cursor: 'pointer' }}
                            />
                          </div>
                        </Col>
                      ))}
                    </Row>
                  </Form.Group>
                </Col>
              </Row>
            </div>

            {(newAgent.agent_type === 'admin_marche' || newAgent.agent_type === 'superviseur') && (
              <div className="mb-4">
                <h6 className="text-muted mb-3 border-bottom pb-2">
                  <Shop className="me-2" size={16} /> Sélection du marché
                </h6>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Marché <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        value={newAgent.id_marche || ''}
                        onChange={(e) => setNewAgent({...newAgent, id_marche: e.target.value || null})}
                        required
                      >
                        <option value="">Sélectionner un marché...</option>
                        {marches.map((marche) => (
                          <option key={marche.id_marche} value={marche.id_marche}>
                            {marche.nom_marche} {marche.code_marche ? `(${marche.id_marche})` : ''}
                          </option>
                        ))}
                      </Form.Select>
                      {loadingMarches && <Spinner animation="border" size="sm" className="ms-2" />}
                      <Form.Text className="text-muted">
                        L'agent aura accès uniquement aux données de ce marché
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </div>
            )}

            {(newAgent.agent_type === 'admin_entite' || newAgent.agent_type === 'admin_principal') && (
              <div className="mb-4">
                <Alert variant="info" className="mb-0">
                  <InfoCircle className="me-2" />
                  <strong>Information :</strong> Les Admins {newAgent.agent_type === 'admin_entite' ? 'Entité' : 'Principal'} n'ont pas de marché assigné.
                  {newAgent.agent_type === 'admin_entite' && " Ils gèrent les utilisateurs hors marché."}
                  {newAgent.agent_type === 'admin_principal' && " Ils gèrent les utilisateurs des marchés."}
                </Alert>
              </div>
            )}

            <div className="mb-4">
              <h6 className="text-muted mb-3 border-bottom pb-2">
                <Person className="me-2" size={16} /> Informations personnelles
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAgent.admin_lname} 
                      onChange={(e) => setNewAgent({...newAgent, admin_lname: e.target.value})} 
                      placeholder="Nom de famille" 
                      required 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAgent.admin_fname} 
                      onChange={(e) => setNewAgent({...newAgent, admin_fname: e.target.value})} 
                      placeholder="Prénom" 
                      required 
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Téléphone <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="tel" 
                      value={newAgent.admin_phone} 
                      onChange={(e) => { 
                        const onlyNumbers = e.target.value.replace(/\D/g, ''); 
                        setNewAgent({...newAgent, admin_phone: onlyNumbers}); 
                      }} 
                      placeholder="XXX XXX XXXX" 
                      required 
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control 
                      type="email" 
                      value={newAgent.email} 
                      onChange={(e) => setNewAgent({...newAgent, email: e.target.value})} 
                      placeholder="exemple@email.com" 
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Fonction <span className="text-danger">*</span></Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAgent.admin_fonction} 
                      onChange={(e) => setNewAgent({...newAgent, admin_fonction: e.target.value})} 
                      placeholder="Fonction" 
                      required 
                    />
                  </Form.Group>
                </Col>
              </Row>
            </div>

            <div className="mb-3">
              <h6 className="text-muted mb-3 border-bottom pb-2">
                <Key className="me-2" size={16} /> Identifiants Internes
              </h6>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Matricule Interne</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAgent.matricule_interne} 
                      onChange={(e) => setNewAgent({...newAgent, matricule_interne: e.target.value})} 
                      placeholder="Ex: MAT-2024-001"
                    />
                    <Form.Text className="text-muted">
                      Identifiant unique interne de l'agent
                    </Form.Text>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Code Agent Interne</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={newAgent.code_agent_interne} 
                      onChange={(e) => setNewAgent({...newAgent, code_agent_interne: e.target.value})} 
                      placeholder="Ex: AG-2024-001"
                    />
                    <Form.Text className="text-muted">
                      Code unique pour identification interne
                    </Form.Text>
                  </Form.Group>
                </Col>
              </Row>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowAddModal(false)} disabled={savingAgent}>Annuler</Button>
            <Button variant="success" onClick={handleSaveAgent} disabled={
              !newAgent.admin_lname.trim() || 
              !newAgent.admin_fname.trim() || 
              !newAgent.admin_phone.trim() || 
              !newAgent.admin_fonction.trim() ||
              ((newAgent.agent_type === 'admin_marche' || newAgent.agent_type === 'superviseur') && !newAgent.id_marche) ||
              savingAgent
            }>
              {savingAgent ? <><Spinner animation="border" size="sm" className="me-2" />Enregistrement...</> : <><Check className="me-2" />Enregistrer</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL D'ÉDITION AVEC CARTES ÉLÉGANTES */}
       {/* MODAL D'ÉDITION - AVEC LES MÊMES RÔLES QUE AddAgent.jsx */}
<Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
  <Modal.Header closeButton className="bg-primary text-white">
    <Modal.Title>
      <Pencil className="me-2" />
      Modifier l'agent
    </Modal.Title>
  </Modal.Header>
  <Modal.Body>
    {selectedAgent && (
      <>
        {/* ============================================================ */}
        {/* TYPES D'AGENTS - CARTES ÉLÉGANTES (IDENTIQUE À AddAgent.jsx) */}
        {/* ============================================================ */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">
            <Shield className="me-2" size={16} /> Type d'agent
          </h6>
          <Row className="g-3">
            {(() => {
              const typeConfig = getAgentTypeConfig();
              const availableTypes = getAvailableAgentTypes();

              return availableTypes.map((typeKey) => {
                const config = typeConfig[typeKey];
                const Icon = config.icon;
                // Déterminer si ce type est sélectionné
                const isSelected = editAgent.agent_type === typeKey;
                return (
                  <Col 
                    key={typeKey} 
                    lg={availableTypes.length === 1 ? 12 : availableTypes.length === 2 ? 6 : 4} 
                    md={availableTypes.length === 1 ? 12 : availableTypes.length === 2 ? 6 : 4} 
                    sm={12}
                  >
                    <div 
                      className={`p-3 rounded-3 h-100 w-100 transition-all ${isSelected ? 'shadow-lg' : 'shadow-sm'}`}
                      style={{
                        backgroundColor: isSelected ? config.bgLight : 'white',
                        border: isSelected ? `2px solid ${config.color}` : '2px solid #e9ecef',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        transform: isSelected ? 'scale(1.02)' : 'scale(1)',
                        position: 'relative'
                      }}
                      onClick={() => {
                        const roleId = config.roleId;
                        setEditAgent({
                          ...editAgent,
                          agent_type: typeKey,
                          role_id: roleId,
                          id_marche: typeKey === 'marche' ? editAgent.id_marche : null
                        });
                      }}
                      onMouseEnter={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = config.color;
                          e.currentTarget.style.backgroundColor = config.bgLight;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.borderColor = '#e9ecef';
                          e.currentTarget.style.backgroundColor = 'white';
                        }
                      }}
                    >
                      {isSelected && (
                        <div className="position-absolute top-0 end-0 m-2">
                          <CheckCircle size={20} color={config.color} />
                        </div>
                      )}
                      <div className="d-flex align-items-start gap-3">
                        <div 
                          className="rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                          style={{
                            width: '48px',
                            height: '48px',
                            backgroundColor: isSelected ? config.color : config.bgLight,
                            color: isSelected ? 'white' : config.color,
                            transition: 'all 0.3s ease'
                          }}
                        >
                          <Icon size={24} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="d-flex align-items-center gap-2">
                            <span className="fw-semibold" style={{ fontSize: '15px' }}>
                              {config.label}
                            </span>
                            <Badge 
                              bg={isSelected ? config.color : 'secondary'} 
                              className="rounded-pill"
                              style={{ 
                                fontSize: '9px',
                                padding: '2px 10px'
                              }}
                            >
                              {config.badge}
                            </Badge>
                          </div>
                          <p className="text-muted small mb-0 mt-1" style={{ fontSize: '12px' }}>
                            {config.description}
                          </p>
                          {typeKey === 'marche' && (
                            <small className="text-primary d-block mt-1" style={{ fontSize: '10px' }}>
                              <Shop size={10} className="me-1" />
                              Nécessite un marché
                            </small>
                          )}
                          {typeKey === 'admin_principal' && (
                            <small className="text-danger d-block mt-1" style={{ fontSize: '10px' }}>
                              <Globe size={10} className="me-1" />
                              Vue consolidée des marchés (ODD/Commune)
                            </small>
                          )}
                          {typeKey === 'admin_entite' && (
                            <small className="text-secondary d-block mt-1" style={{ fontSize: '10px' }}>
                              <Building size={10} className="me-1" />
                              Pas de marché assigné
                            </small>
                          )}
                          {typeKey === 'hors_marche' && (
                            <small className="text-secondary d-block mt-1" style={{ fontSize: '10px' }}>
                              <Building size={10} className="me-1" />
                              Pas de marché assigné
                            </small>
                          )}
                        </div>
                      </div>
                    </div>
                  </Col>
                );
              });
            })()}
          </Row>
        </div>

        {/* ============================================================ */}
        {/* SÉLECTION DU MARCHÉ - IDENTIQUE À AddAgent.jsx */}
        {/* ============================================================ */}
        {editAgent.agent_type === 'marche' && (
          <div className="mb-4">
            <h6 className="text-muted mb-3 border-bottom pb-2">
              <Shop className="me-2" size={16} /> Sélection du marché
            </h6>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold">
                    Marché <span className="text-danger">*</span>
                  </Form.Label>
                  
                  {/* SI L'UTILISATEUR A UN MARCHÉ PAR DÉFAUT */}
                  {hasMarket ? (
                    <div className="p-3 bg-light rounded-3 border border-primary">
                      <div className="d-flex align-items-center">
                        <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                          <Shop className="text-primary" size={20} />
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-bold fs-5">
                            {marches.find(m => m.id_marche === userMarcheId)?.nom_marche || userMarcheId}
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2 mt-1">
                            <Badge bg="light" text="dark" className="rounded-pill">
                              ID: {userMarcheId}
                            </Badge>
                            {marches.find(m => m.id_marche === userMarcheId)?.code_marche && (
                              <Badge bg="light" text="dark" className="rounded-pill">
                                Code: {marches.find(m => m.id_marche === userMarcheId)?.code_marche}
                              </Badge>
                            )}
                            <Badge bg="success" className="rounded-pill">
                              <CheckCircle size={12} className="me-1" />
                              Marché par défaut
                            </Badge>
                          </div>
                          <small className="text-muted d-block mt-1">
                            <InfoCircle size={12} className="me-1" />
                            Ce marché est lié à votre compte et sera automatiquement assigné à l'agent
                          </small>
                        </div>
                        <div className="ms-3">
                          <Badge bg="primary" className="rounded-pill px-3 py-2">
                            <Check size={14} className="me-1" />
                            Assigné
                          </Badge>
                        </div>
                      </div>
                      <Form.Control 
                        type="hidden" 
                        name="id_marche"
                        value={editAgent.id_marche || userMarcheId}
                      />
                    </div>
                  ) : (
                    /* SELECT AVEC RECHERCHE POUR LES AUTRES */
                    <>
                      <div ref={marcheSearchRef} className="position-relative">
                        <InputGroup>
                          <InputGroup.Text className="bg-light border-end-0">
                            <Search size={16} className="text-primary" />
                          </InputGroup.Text>
                          <Form.Control
                            type="text"
                            placeholder="Rechercher un marché par nom, ID ou code..."
                            value={marcheSearchTerm}
                            onChange={(e) => {
                              setMarcheSearchTerm(e.target.value);
                              setShowMarcheDropdown(true);
                              if (!e.target.value) {
                                setEditAgent(prev => ({ ...prev, id_marche: null }));
                                setSelectedMarcheName('');
                              }
                            }}
                            onFocus={() => setShowMarcheDropdown(true)}
                            className="border-start-0 bg-light"
                            isInvalid={editAgent.agent_type === 'marche' && !editAgent.id_marche}
                          />
                          {editAgent.id_marche && (
                            <Button 
                              variant="outline-secondary" 
                              className="border-start-0"
                              onClick={() => {
                                setMarcheSearchTerm('');
                                setSelectedMarcheName('');
                                setEditAgent(prev => ({ ...prev, id_marche: null }));
                                setShowMarcheDropdown(false);
                              }}
                            >
                              <X size={16} />
                            </Button>
                          )}
                        </InputGroup>
                        
                        {showMarcheDropdown && (
                          <div 
                            className="position-absolute w-100 mt-1 bg-white rounded-3 shadow-lg border"
                            style={{ zIndex: 1000, maxHeight: '250px', overflowY: 'auto' }}
                          >
                            {loadingMarches ? (
                              <div className="p-3 text-center">
                                <Spinner animation="border" size="sm" />
                                <span className="ms-2">Chargement...</span>
                              </div>
                            ) : getFilteredMarches(marcheSearchTerm).length > 0 ? (
                              getFilteredMarches(marcheSearchTerm).map(marche => (
                                <div
                                  key={marche.id_marche}
                                  className="p-2 border-bottom hover-bg-light cursor-pointer d-flex justify-content-between align-items-center"
                                  onClick={() => {
                                    setEditAgent(prev => ({ ...prev, id_marche: marche.id_marche }));
                                    setSelectedMarcheName(marche.nom_marche);
                                    setMarcheSearchTerm(marche.nom_marche);
                                    setShowMarcheDropdown(false);
                                  }}
                                  style={{ cursor: 'pointer' }}
                                >
                                  <div className="d-flex align-items-center gap-2">
                                    <Shop className="text-primary" size={16} />
                                    <div>
                                      <span className="fw-medium">{marche.nom_marche}</span>
                                      <br />
                                      <small className="text-muted">
                                        ID: {marche.id_marche} 
                                        {marche.code_marche && ` • Code: ${marche.code_marche}`}
                                        {marche.quartier?.nom && ` • ${marche.quartier.nom}`}
                                      </small>
                                    </div>
                                  </div>
                                  <div>
                                    {editAgent.id_marche === marche.id_marche && (
                                      <CheckCircle className="text-success" size={18} />
                                    )}
                                    <Badge 
                                      bg={marche.etat === 'Ouvert' ? 'success' : 'danger'} 
                                      className="ms-2 rounded-pill"
                                      style={{ fontSize: '8px' }}
                                    >
                                      {marche.etat || 'Ouvert'}
                                    </Badge>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="p-3 text-center text-muted">
                                <ExclamationCircle className="me-2" />
                                Aucun marché trouvé
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {selectedMarcheName && (
                        <div className="mt-2">
                          <Badge bg="success" className="px-3 py-2 rounded-pill">
                            <CheckCircle className="me-1" size={12} />
                            Marché sélectionné : {selectedMarcheName}
                          </Badge>
                        </div>
                      )}
                      <Form.Text className="text-muted">
                        L'agent aura accès uniquement aux données de ce marché
                      </Form.Text>
                      {editAgent.agent_type === 'marche' && !editAgent.id_marche && (
                        <Form.Text className="text-danger d-block">
                          <ExclamationCircle className="me-1" size={12} />
                          Veuillez sélectionner un marché
                        </Form.Text>
                      )}
                    </>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
        )}

        {(editAgent.agent_type === 'admin_entite' || editAgent.agent_type === 'admin_principal') && (
          <div className="mb-4">
            <Alert variant="info" className="mb-0">
              <InfoCircle className="me-2" />
              <strong>Information :</strong> Les Admins {editAgent.agent_type === 'admin_entite' ? 'Entité' : 'Principal'} n'ont pas de marché assigné.
              {editAgent.agent_type === 'admin_entite' && " Ils gèrent les utilisateurs hors marché."}
              {editAgent.agent_type === 'admin_principal' && " Ils gèrent les utilisateurs des marchés (ODD/Commune)."}
            </Alert>
          </div>
        )}

        {editAgent.agent_type === 'hors_marche' && (
          <div className="mb-4">
            <Alert variant="secondary" className="mb-0">
              <InfoCircle className="me-2" />
              <strong>Information :</strong> L'agent hors marché n'a pas de marché assigné. Il collecte hors marché.
            </Alert>
          </div>
        )}

        {/* ============================================================ */}
        {/* INFORMATIONS PERSONNELLES - IDENTIQUE À AddAgent.jsx */}
        {/* ============================================================ */}
        <div className="mb-4">
          <h6 className="text-muted mb-3 border-bottom pb-2">
            <Person className="me-2" size={16} /> Informations personnelles
          </h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Nom <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="text" 
                  value={editAgent.admin_lname || ''} 
                  onChange={(e) => {
                    setEditAgent({...editAgent, admin_lname: e.target.value, full_name: e.target.value});
                  }}
                  placeholder="Nom de famille"
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Prénom <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="text" 
                  value={editAgent.admin_fname || ''} 
                  onChange={(e) => {
                    setEditAgent({...editAgent, admin_fname: e.target.value, first_name: e.target.value});
                  }}
                  placeholder="Prénom"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="email" 
                  value={editAgent.email || ''} 
                  onChange={(e) => setEditAgent({...editAgent, email: e.target.value})}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Téléphone <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="tel" 
                  value={editAgent.admin_phone || ''} 
                  onChange={(e) => {
                    let onlyNumbers = e.target.value.replace(/\D/g, '');
                    setEditAgent({...editAgent, admin_phone: onlyNumbers});
                  }}
                  placeholder="XXX XXX XXXX"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group className="mb-3">
                <Form.Label>Fonction <span className="text-danger">*</span></Form.Label>
                <Form.Control 
                  type="text" 
                  value={editAgent.admin_fonction || ''} 
                  onChange={(e) => setEditAgent({...editAgent, admin_fonction: e.target.value})}
                  placeholder="Fonction"
                  required
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* ============================================================ */}
        {/* IDENTIFIANTS INTERNES - IDENTIQUE À AddAgent.jsx */}
        {/* ============================================================ */}
        <div className="mb-3">
          <h6 className="text-muted mb-3 border-bottom pb-2">
            <Key className="me-2" size={16} /> Identifiants Internes
          </h6>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Matricule Interne</Form.Label>
                <Form.Control 
                  type="text" 
                  value={editAgent.matricule_interne || ''} 
                  onChange={(e) => setEditAgent({...editAgent, matricule_interne: e.target.value})}
                  placeholder="Ex: MAT-2024-001"
                />
                <Form.Text className="text-muted">
                  Identifiant unique interne de l'agent
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Code Agent Interne</Form.Label>
                <Form.Control 
                  type="text" 
                  value={editAgent.code_agent_interne || ''} 
                  onChange={(e) => setEditAgent({...editAgent, code_agent_interne: e.target.value})}
                  placeholder="Ex: AG-2024-001"
                />
                <Form.Text className="text-muted">
                  Code unique pour identification interne
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </div>
      </>
    )}
  </Modal.Body>
  <Modal.Footer>
    <Button variant="secondary" onClick={() => setShowEditModal(false)} disabled={updatingAgent}>
      <X className="me-2" /> Annuler
    </Button>
    <Button 
      variant="primary" 
      onClick={handleUpdateAgent} 
      disabled={
        !editAgent.email?.trim() || 
        !editAgent.admin_fname?.trim() || 
        !editAgent.admin_lname?.trim() ||
        !editAgent.admin_phone?.trim() ||
        !editAgent.admin_fonction?.trim() ||
        (editAgent.agent_type === 'marche' && !editAgent.id_marche) ||
        updatingAgent
      }
    >
      {updatingAgent ? (
        <><Spinner animation="border" size="sm" className="me-2" />Mise à jour...</>
      ) : (
        <><Save className="me-2" size={16} /> Mettre à jour</>
      )}
    </Button>
  </Modal.Footer>
</Modal>

        {/* MODAL DE SUPPRESSION */}
        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton><Modal.Title><ExclamationCircle className="text-warning me-2" />Confirmer la suppression</Modal.Title></Modal.Header>
          <Modal.Body className="text-center"><Trash size={48} className="text-danger mb-3" /><h5>Êtes-vous sûr de vouloir supprimer cet agent ?</h5>{agentToDelete && (<div className="mt-3 p-3 bg-light rounded"><strong>{agentToDelete.full_name}</strong><br /><small className="text-muted">{agentToDelete.email}</small><br /><small className="text-muted">Rôle: {agentToDelete.role}</small></div>)}<p className="text-muted mt-3">Cette action est irréversible et supprimera définitivement l'agent.</p></Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deletingAgent}><X className="me-2" />Annuler</Button><Button variant="danger" onClick={handleConfirmDelete} disabled={deletingAgent}>{deletingAgent ? <><Spinner animation="border" size="sm" className="me-2" />Suppression...</> : <><Trash className="me-2" />Supprimer définitivement</>}</Button></Modal.Footer>
        </Modal>

        {/* MODAL DE RESTAURATION */}
        <Modal show={showRestoreModal} onHide={() => setShowRestoreModal(false)} centered>
          <Modal.Header closeButton><Modal.Title><ArrowRepeat className="text-success me-2" />Restaurer l'agent</Modal.Title></Modal.Header>
          <Modal.Body className="text-center"><ArrowRepeat size={48} className="text-success mb-3" /><h5>Restaurer cet agent ?</h5>{agentToRestore && (<div className="mt-3 p-3 bg-light rounded"><strong>{agentToRestore.full_name}</strong><br /><small className="text-muted">{agentToRestore.email}</small></div>)}<p className="text-muted mt-3">L'agent sera réactivé et pourra se connecter.</p></Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowRestoreModal(false)} disabled={restoringAgent}><X className="me-2" />Annuler</Button><Button variant="success" onClick={handleConfirmRestore} disabled={restoringAgent}>{restoringAgent ? <><Spinner animation="border" size="sm" className="me-2" />Restauration...</> : <><ArrowRepeat className="me-2" />Restaurer</>}</Button></Modal.Footer>
        </Modal>

        {/* MODAL DE SUPPRESSION MULTIPLE */}
        <Modal show={showBulkDeleteModal} onHide={() => setShowBulkDeleteModal(false)} centered>
          <Modal.Header closeButton><Modal.Title><ExclamationCircle className="text-warning me-2" />Confirmer la suppression multiple</Modal.Title></Modal.Header>
          <Modal.Body className="text-center"><Trash size={48} className="text-danger mb-3" /><h5>Supprimer {selectedAgents.length} agent(s) ?</h5><p className="text-muted mt-3">Vous êtes sur le point de supprimer définitivement {selectedAgents.length} agent(s).<br /><strong>Cette action est irréversible.</strong></p></Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowBulkDeleteModal(false)} disabled={deletingAgents}><X className="me-2" />Annuler</Button><Button variant="danger" onClick={handleConfirmBulkDelete} disabled={deletingAgents}>{deletingAgents ? <><Spinner animation="border" size="sm" className="me-2" />Suppression...</> : <><Trash className="me-2" />Supprimer {selectedAgents.length} agent(s)</>}</Button></Modal.Footer>
        </Modal>

        {/* MODAL DE RESTAURATION MULTIPLE */}
        <Modal show={showBulkRestoreModal} onHide={() => setShowBulkRestoreModal(false)} centered>
          <Modal.Header closeButton><Modal.Title><ArrowRepeat className="text-success me-2" />Confirmer la restauration multiple</Modal.Title></Modal.Header>
          <Modal.Body className="text-center"><ArrowRepeat size={48} className="text-success mb-3" /><h5>Restaurer {selectedAgents.length} agent(s) ?</h5><p className="text-muted mt-3">Vous êtes sur le point de restaurer {selectedAgents.length} agent(s).<br /><strong>Ces agents seront réactivés.</strong></p></Modal.Body>
          <Modal.Footer><Button variant="secondary" onClick={() => setShowBulkRestoreModal(false)} disabled={restoringAgents}><X className="me-2" />Annuler</Button><Button variant="success" onClick={handleConfirmBulkRestore} disabled={restoringAgents}>{restoringAgents ? <><Spinner animation="border" size="sm" className="me-2" />Restauration...</> : <><ArrowRepeat className="me-2" />Restaurer {selectedAgents.length} agent(s)</>}</Button></Modal.Footer>
        </Modal>

        {/* MODAL DE RÉINITIALISATION DE MOT DE PASSE */}
        <Modal show={showResetPasswordModal} onHide={() => !resettingPassword && setShowResetPasswordModal(false)} centered>
          <Modal.Header closeButton className="bg-info text-white">
            <Modal.Title><Key className="me-2" />Réinitialiser le mot de passe</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="text-center">
              <Key size={48} className="text-info mb-3" />
              <h5>Réinitialiser le mot de passe ?</h5>
              {agentToResetPassword && (
                <div className="mt-3 p-3 bg-light rounded">
                  <strong>{agentToResetPassword.full_name}</strong>
                  <br />
                  <small className="text-muted">{agentToResetPassword.email}</small>
                </div>
              )}
              <p className="text-muted mt-3">
                Un nouveau mot de passe temporaire à 6 chiffres sera généré.
                <br />
                <strong>Ce mot de passe devra être changé lors de la prochaine connexion.</strong>
              </p>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowResetPasswordModal(false)} disabled={resettingPassword}>
              <X className="me-2" />Annuler
            </Button>
            <Button variant="info" onClick={handleConfirmResetPassword} disabled={resettingPassword}>
              {resettingPassword ? <><Spinner animation="border" size="sm" className="me-2" />Réinitialisation...</> : <><Key className="me-2" />Réinitialiser</>}
            </Button>
          </Modal.Footer>
        </Modal>

        {/* MODAL D'AFFICHAGE DU MOT DE PASSE */}
        <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} centered size="lg">
          <Modal.Header closeButton className="bg-success text-white">
            <Modal.Title><CheckCircle className="me-2" />Mot de passe réinitialisé</Modal.Title>
          </Modal.Header>
          <Modal.Body className="py-4">
            <div className="mb-4">
              <div className="bg-light p-3 rounded-3">
                <small className="text-muted text-uppercase">Utilisateur</small>
                <h5 className="mb-0">{resetPasswordResult?.username || agentToResetPassword?.email}</h5>
                <small className="text-muted d-block mt-1">📱 {resetPasswordResult?.phone || agentToResetPassword?.admin_phone || 'Non spécifié'}</small>
              </div>
            </div>
            
            <div className="mb-4">
              <h6 className="fw-semibold mb-2"><Shield className="me-2 text-primary" size={16} />Statut de l'envoi SMS</h6>
              {resetPasswordResult?.sms_sent === true && (
                <Alert variant="success" className="mb-2">
                  <div className="d-flex align-items-start">
                    <CheckCircle className="me-2 mt-1" size={18} />
                    <div><strong>SMS envoyé avec succès !</strong><br /><small>Un SMS contenant le nouveau mot de passe a été envoyé à {resetPasswordResult?.phone || 'l\'utilisateur'}</small></div>
                  </div>
                </Alert>
              )}
              {resetPasswordResult?.sms_sent === false && (
                <Alert variant="warning" className="mb-2">
                  <div className="d-flex align-items-start">
                    <ExclamationCircle className="me-2 mt-1" size={18} />
                    <div><strong>Attention - SMS non envoyé</strong><br /><small>{resetPasswordResult?.sms_error || 'Erreur lors de l\'envoi du SMS. Vérifiez le numéro de téléphone.'}</small><div className="mt-2 pt-2 border-top"><small className="text-muted"><strong>Le mot de passe a bien été réinitialisé mais n'a pas pu être envoyé par SMS.</strong><br />Veuillez communiquer le mot de passe manuellement à l'utilisateur.</small></div></div>
                  </div>
                </Alert>
              )}
              {resetPasswordResult?.sms_sent === undefined && (
                <Alert variant="info" className="mb-2">
                  <div className="d-flex align-items-start">
                    <InfoCircle className="me-2 mt-1" size={18} />
                    <div><strong>Informations SMS</strong><br /><small>Le mot de passe a été réinitialisé. Assurez-vous de le communiquer à l'utilisateur.</small></div>
                  </div>
                </Alert>
              )}
            </div>
            
            <Alert variant="info" className="mt-2 text-start">
              <InfoCircle className="me-2" />
              <strong>Information importante :</strong><br />
              Ce mot de passe est temporaire. L'utilisateur devra le changer lors de sa prochaine connexion.
              <hr className="my-2" />
              <small>ID: {resetPasswordResult?.user_id || agentToResetPassword?.user_id}</small>
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>Fermer</Button>
            <Button variant="success" onClick={() => { navigator.clipboard.writeText(resetPasswordResult?.password_show); showNotification('Mot de passe copié dans le presse-papier', 'success'); setShowPasswordModal(false); }}><Copy className="me-2" />Copier et fermer</Button>
          </Modal.Footer>
        </Modal>
        
        <Footer />
      </div>
    </React.Fragment>
  );
}